<template>
  <div class="tl">
    <Breadcrumbs main="Пользователи" title="Уровни и опыт"/>

    <div class="container-fluid tl-container">

      <!-- ================== TOOLBAR ================== -->
      <div class="tl-toolbar">
        <div class="tl-toolbar__main">
          <div class="tl-search" :class="{ 'is-busy': isLoadingLevels }">
            <i class="fa fa-search tl-search__icon"></i>
            <input
                ref="search"
                type="search"
                class="tl-search__input"
                v-model="searchModel"
                placeholder="ID, username, почта, команда, PUBG ID или ник…"
                @keydown.esc.prevent="clearSearch"
            />
            <button v-if="searchModel" type="button" class="tl-search__clear" @click="clearSearch">
              <i class="fa fa-times"></i>
            </button>
            <kbd v-else class="tl-search__kbd">/</kbd>
          </div>

          <div class="tl-spacer"></div>

          <button type="button" class="tl-icon-btn" :class="{ 'is-on': density === 'compact' }"
                  @click="toggleDensity" v-b-tooltip.hover.bottom="'Плотность строк'">
            <i class="fa fa-bars"></i>
          </button>

          <router-link :to="{ name: 'users_real' }" class="tl-icon-btn"
                       v-b-tooltip.hover.bottom="'Реальные пользователи'">
            <i class="fa fa-users"></i>
          </router-link>

          <button type="button" class="tl-icon-btn" :class="{ 'is-spin': isLoadingLevels }"
                  @click="refresh" v-b-tooltip.hover.bottom="'Обновить'">
            <i class="fa fa-refresh"></i>
          </button>
        </div>

        <div v-if="searchApplied" class="tl-chips">
          <span class="tl-chips__label">Фильтры:</span>
          <button type="button" class="tl-chip" @click="clearSearch">
            <i class="fa fa-search"></i> «{{ searchApplied }}» <i class="fa fa-times tl-chip__x"></i>
          </button>
          <button type="button" class="tl-chips__reset" @click="clearSearch">Сбросить всё</button>
        </div>
      </div>

      <!-- ================== CONTENT ================== -->
      <div class="tl-panel" :class="['tl-panel--' + density]">

        <div v-if="isLoadingLevels" class="tl-skeleton">
          <div v-for="n in 8" :key="'sk' + n" class="tl-skeleton__row">
            <span class="tl-sk tl-sk--avatar"></span>
            <span class="tl-sk tl-sk--wide"></span>
            <span class="tl-sk"></span>
            <span class="tl-sk"></span>
            <span class="tl-sk tl-sk--sm"></span>
          </div>
        </div>

        <div v-else-if="!rows.length" class="tl-empty">
          <div class="tl-empty__art"><i class="fa fa-signal"></i></div>
          <div class="tl-empty__title">
            {{ searchApplied ? 'Ничего не нашлось' : 'Данных нет' }}
          </div>
          <div class="tl-empty__text">
            {{ searchApplied
              ? 'Попробуйте изменить условия поиска.'
              : 'Здесь появятся пользователи с игровыми профилями и накопленным опытом.' }}
          </div>
          <div v-if="searchApplied" class="tl-empty__actions">
            <button type="button" class="tl-btn-ghost-2" @click="clearSearch">Сбросить поиск</button>
          </div>
        </div>

        <div v-else class="tl-table-wrap">
          <b-table
              ref="table"
              class="tl-table"
              responsive
              hover
              borderless
              show-empty
              :items="rows"
              :fields="fields"
              @row-clicked="openUser"
          >
            <template #cell(id)="{ item }">
              <button type="button" class="tl-id" @click.stop="copyId(item.id)"
                      v-b-tooltip.hover.right="'Скопировать ID'">
                <span>#{{ item.id }}</span>
                <i class="fa fa-copy"></i>
              </button>
            </template>

            <template #cell(username)="{ item }">
              <div class="tl-name">
                <span class="tl-name__logo">{{ initials(item.username) }}</span>
                <button type="button" class="tl-name__title" @click.stop="openUser(item)" :title="item.username">
                  {{ item.username || '—' }}
                </button>
              </div>
            </template>

            <template #cell(levels)="{ item }">
              <div v-if="item.x_profiles.length" class="tl-levels">
                <button v-for="profile in item.x_profiles" :key="'lvl' + profile.id"
                        type="button" class="tl-level"
                        @click.stop="openLevelLogs(profile.id)"
                        v-b-tooltip.hover.top="'История начислений опыта'">
                  <span class="tl-level__game">{{ profile.game }}</span>
                  <span class="tl-level__num">{{ profile.level }}</span>
                  <span class="tl-level__xp">{{ profile.experience }} XP</span>
                </button>
              </div>
              <span v-else class="tl-muted">игровых профилей нет</span>
            </template>

            <template #cell(created_at)="{ item }">
              <div class="tl-time">
                <span class="tl-time__abs">{{ formatDate(item.created_at) }}</span>
                <span class="tl-time__rel">{{ formatRelative(item.created_at) }}</span>
              </div>
            </template>

            <template #cell(action)="{ item }">
              <div class="tl-actions" @click.stop>
                <button type="button" class="tl-act" @click="openUser(item)"
                        v-b-tooltip.hover.top="'Открыть карточку в новой вкладке'">
                  <i class="fa fa-eye"></i>
                </button>
              </div>
            </template>

            <template #empty>
              <div class="tl-empty tl-empty--inline">Ничего не найдено</div>
            </template>
          </b-table>
        </div>

        <div v-if="!isLoadingLevels && rows.length" class="tl-foot">
          <div class="tl-foot__info">
            Показано <b>{{ rangeFrom }}–{{ rangeTo }}</b> из <b>{{ totalUsersLevelsPagination || 0 }}</b>
          </div>
          <b-pagination
              class="tl-pagination my-0"
              v-model="currentPageModel"
              :total-rows="totalUsersLevelsPagination"
              :per-page="perPage"
              :limit="7"
              @change="goToPage"
          />
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import {UsersAdapterMixin} from "../../mixins/Users/UsersAdapterMixin";
import {PaginationHelperMixin} from "@/mixins/Helpers/PaginationHelperMixin";

const LS = {density: "usersLevelsDensity"};

export default {
  name: "ListUsersLevels",
  mixins: [UsersAdapterMixin, PaginationHelperMixin],
  data() {
    return {
      pageKey: 'pageNumberUserslevelsList',
      perPage: 10,

      searchModel: '',
      searchApplied: '',
      searchTimer: null,

      density: 'cozy',
      isLoadingLevels: false,

      fields: [
        {key: 'id', label: 'ID'},
        {key: 'username', label: 'Пользователь', sortable: true},
        {key: 'levels', label: 'Уровень и опыт'},
        {key: 'created_at', label: 'Аккаунт создан', sortable: true},
        {key: 'action', label: '', thClass: 'tl-th--act', tdClass: 'tl-td--act'},
      ],
    }
  },
  computed: {
    /** Игровые профили сводим к готовым чипам «дисциплина — уровень — опыт». */
    rows() {
      return (this.usersLevels || []).map(user => ({
        ...user,
        x_profiles: (user.gameProfiles || [])
            .filter(Boolean)
            .map(profile => ({
              id: profile.id,
              game: (profile.game && profile.game.name) || '—',
              level: (profile.level && profile.level.number) != null ? profile.level.number : '—',
              experience: profile.experience_points != null ? profile.experience_points : 0,
            })),
      }));
    },

    currentPageModel: {
      get() {
        return Number(this.usersLevelsCurrentPage) || 1;
      },
      set(value) {
        this.usersLevelsCurrentPage = value;
      },
    },

    rangeFrom() {
      if (!this.totalUsersLevelsPagination) return 0;
      return (this.currentPageModel - 1) * this.perPage + 1;
    },

    rangeTo() {
      return Math.min(this.currentPageModel * this.perPage, this.totalUsersLevelsPagination || 0);
    },
  },
  watch: {
    searchModel(value) {
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (this.searchApplied === value.trim()) return;
        this.searchApplied = value.trim();
        this.usersLevelsCurrentPage = 1;
        this.fetchLevels();
      }, 400);
    },
  },
  mounted() {
    this.restorePreferences();
    this.usersLevelsCurrentPage = Number(this.getCurrentPageNumber(this.pageKey)) || 1;
    this.fetchLevels();
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onKeydown);
    if (this.searchTimer) clearTimeout(this.searchTimer);
  },
  methods: {
    async fetchLevels() {
      this.isLoadingLevels = true;
      this.recordPageToStorage(this.currentPageModel, this.pageKey);
      try {
        await this.grabUsersLevels({
          pageNumber: this.currentPageModel,
          ...(this.searchApplied && {search: this.searchApplied}),
        });
      } catch (e) {
        this.$toasted.error("Не удалось загрузить уровни", {position: 'top-right', duration: 3000});
      } finally {
        this.isLoadingLevels = false;
      }
    },

    refresh() {
      this.fetchLevels();
    },

    goToPage(pageNumber) {
      this.usersLevelsCurrentPage = pageNumber;
      this.$nextTick(this.fetchLevels);
    },

    clearSearch() {
      this.searchModel = '';
      if (!this.searchApplied) return;
      this.searchApplied = '';
      this.usersLevelsCurrentPage = 1;
      this.fetchLevels();
    },

    openUser(item) {
      this.showUserInfo(item.id);
    },

    openLevelLogs(gameProfileId) {
      this.$router.push({name: 'user_levels_logs', params: {id: gameProfileId}});
    },

    copyId(id) {
      const done = () => this.$toasted.success(`ID ${id} скопирован`, {position: 'top-right', duration: 1500});
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(String(id)).then(done).catch(() => {
        });
        return;
      }
      const area = document.createElement("textarea");
      area.value = String(id);
      document.body.appendChild(area);
      area.select();
      try {
        document.execCommand("copy");
        done();
      } finally {
        document.body.removeChild(area);
      }
    },

    initials(name) {
      if (!name) return "?";
      return String(name).trim().slice(0, 2).toUpperCase();
    },

    formatDate(value) {
      if (!value) return "—";
      const date = new Date(String(value).replace(" ", "T"));
      if (isNaN(date.getTime())) return "—";
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },

    formatRelative(value) {
      if (!value) return "";
      const date = new Date(String(value).replace(" ", "T"));
      if (isNaN(date.getTime())) return "";
      const days = Math.round((Date.now() - date.getTime()) / 86400000);
      if (days <= 0) return "сегодня";
      if (days === 1) return "вчера";
      if (days < 30) return `${days} дн. назад`;
      const months = Math.round(days / 30);
      if (months < 12) return `${months} мес. назад`;
      return `${Math.round(months / 12)} г. назад`;
    },

    restorePreferences() {
      try {
        const density = localStorage.getItem(LS.density);
        if (density === "compact" || density === "cozy") this.density = density;
      } catch (e) { /* повреждённые настройки — берём значения по умолчанию */
      }
    },

    toggleDensity() {
      this.density = this.density === "compact" ? "cozy" : "compact";
      localStorage.setItem(LS.density, this.density);
    },

    onKeydown(event) {
      const tag = (event.target && event.target.tagName) || "";
      const isTyping = /INPUT|TEXTAREA|SELECT/.test(tag) || (event.target && event.target.isContentEditable);

      if (event.key === "Escape") {
        if (isTyping) return;
        if (this.searchApplied) this.clearSearch();
        return;
      }

      if (isTyping || event.ctrlKey || event.metaKey || event.altKey) return;

      if (event.key === "/") {
        event.preventDefault();
        if (this.$refs.search) this.$refs.search.focus();
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/admin-list.scss";

.tl-name__title {
  border: 0;
  background: none;
  padding: 0;
  text-align: left;
  cursor: pointer;
}

.tl-name__logo {
  font-weight: 700;
  font-size: 12px;
}

.tl-levels {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.tl-level {
  display: inline-flex;
  align-items: baseline;
  gap: 7px;
  padding: 3px 10px;
  border: 1px solid var(--tl-border);
  border-radius: 9px;
  background: var(--tl-soft);
  cursor: pointer;
  transition: all .15s ease;

  &:hover {
    border-color: var(--tl-primary);
    background: var(--tl-primary-soft);
  }

  &__game {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: var(--tl-muted);
  }

  &__num {
    font-size: 14px;
    font-weight: 700;
    color: var(--tl-primary);
    font-variant-numeric: tabular-nums;
  }

  &__xp {
    font-size: 11px;
    color: var(--tl-muted);
    font-variant-numeric: tabular-nums;
  }
}
</style>
